// Using global signalR from index.html CDN to bypass npm issues
declare const signalR: any;

const HUB_URL = 'https://tetfundtagapi.lloydant.com/submissionCommunicationHub';

export enum ConnectionStatus {
    Connected = 'Connected',
    Disconnected = 'Disconnected',
    Reconnecting = 'Reconnecting',
    Connecting = 'Connecting'
}

class SignalRService {
    private connection: any = null;
    private callbacks: Map<string, ((data: any) => void)[]> = new Map();
    private stateCallbacks: ((status: ConnectionStatus) => void)[] = [];

    async startConnection() {
        if (this.connection) return;

        const token = localStorage.getItem('token');

        if (typeof signalR === 'undefined') {
            return;
        }

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, {
                accessTokenFactory: () => token || '',
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets
            })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Trace)
            .build();

        this.connection.onreconnecting((error: any) => {
            this.notifyStateChange(ConnectionStatus.Reconnecting);
        });

        this.connection.onreconnected((connectionId: string) => {
            this.notifyStateChange(ConnectionStatus.Connected);
        });

        this.connection.onclose((error: any) => {
            this.notifyStateChange(ConnectionStatus.Disconnected);
            this.connection = null;
        });

        try {
            this.notifyStateChange(ConnectionStatus.Connecting);
            await this.connection.start();
            this.notifyStateChange(ConnectionStatus.Connected);

            // Listen for both event names to ensure compatibility
            const handleMessage = (data: any) => {
                const eventCallbacks = this.callbacks.get('SubmissionCommunication');
                if (eventCallbacks) eventCallbacks.forEach(cb => cb(data));
            };

            this.connection.on('SubmissionCommunication', handleMessage);
            this.connection.on('PersonalNotification', handleMessage);
            this.connection.on('personalnotification', handleMessage); // Adding lowercase just in case

        } catch (err) {
            this.notifyStateChange(ConnectionStatus.Disconnected);
            this.connection = null;
        }
    }

    private notifyStateChange(status: ConnectionStatus) {
        this.stateCallbacks.forEach(cb => cb(status));
    }

    onStateChange(callback: (status: ConnectionStatus) => void) {
        this.stateCallbacks.push(callback);
        if (this.connection) {
            callback(this.connection.state === 'Connected' ? ConnectionStatus.Connected :
                this.connection.state === 'Reconnecting' ? ConnectionStatus.Reconnecting :
                    ConnectionStatus.Connecting);
        } else {
            callback(ConnectionStatus.Disconnected);
        }
        return () => {
            this.stateCallbacks = this.stateCallbacks.filter(cb => cb !== callback);
        };
    }

    async stopConnection() {
        if (this.connection) {
            await this.connection.stop();
            this.connection = null;
        }
    }

    // Backend confirmed Groups are no longer used
    async joinGroup(submissionId: string) {
        if (this.connection && this.connection.state === 'Connected') {
            try {
                await this.connection.invoke("JoinSubmission", submissionId);
                } catch (err) {
                }
        }
    }

    async leaveGroup(submissionId: string) {
        if (this.connection && this.connection.state === 'Connected') {
            try {
                await this.connection.invoke("LeaveSubmission", submissionId);
                } catch (err) {
                }
        }
    }

    onReceiveMessage(callback: (data: any) => void) {
        const eventName = 'SubmissionCommunication';
        if (!this.callbacks.has(eventName)) {
            this.callbacks.set(eventName, []);
        }
        this.callbacks.get(eventName)?.push(callback);

        return () => {
            const list = this.callbacks.get(eventName);
            if (list) {
                this.callbacks.set(eventName, list.filter(cb => cb !== callback));
            }
        };
    }

    getConnectionState(): ConnectionStatus {
        if (!this.connection) return ConnectionStatus.Disconnected;
        switch (this.connection.state) {
            case 'Connected': return ConnectionStatus.Connected;
            case 'Reconnecting': return ConnectionStatus.Reconnecting;
            case 'Connecting': return ConnectionStatus.Connecting;
            default: return ConnectionStatus.Disconnected;
        }
    }
}

const signalRService = new SignalRService();
export default signalRService;
